<script setup lang="ts">
/*
 * Global in-flight-request progress bar — an NProgress / YouTube-style thin
 * (3px) bar fixed to the very top of the viewport, above the app shell, the
 * TopBar, and any drawer/modal (z-index above everything). Mounted ONCE at the
 * app root (App.vue) so it covers BOTH the pre-auth routes (the /welcome
 * sign-in-config lookup) and the authed shell.
 *
 * It reads `requestActivity.isActive` (the shared counter driven by the axios
 * interceptors on httpClient + iamPublic) and runs it through
 * `useProgressController`, which owns ALL timing: debounce-show (no flash for
 * sub-180ms requests), trickle toward ~90%, min-visible window (no strobing on
 * fast bursts), and snap-to-100% + fade on settle.
 *
 * Accessibility: the bar exposes role=progressbar + aria-value*, and a
 * visually-hidden aria-live region announces "Loading…" while active and clears
 * on settle. It respects prefers-reduced-motion — no trickle animation, just a
 * steady indeterminate fill that shows/hides.
 *
 * Colour + radius come from tokens only (--color-primary via bg-primary); no
 * hex literals (CLAUDE.md "Token graph is proto-canonical").
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useRequestActivityStore } from '@/stores/requestActivity';
import { useProgressController } from '@/composables/useProgressController';

const { t } = useI18n();

const store = useRequestActivityStore();
const { isActive } = storeToRefs(store);

// Detect prefers-reduced-motion at mount (guard for happy-dom / SSR where
// matchMedia may be absent). Re-check live so a mid-session OS toggle is honored.
const reducedMotion = ref(false);
let mq: MediaQueryList | null = null;
function syncReducedMotion(): void {
  reducedMotion.value = mq?.matches ?? false;
}
onMounted(() => {
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    syncReducedMotion();
    mq.addEventListener?.('change', syncReducedMotion);
  }
});
onBeforeUnmount(() => {
  mq?.removeEventListener?.('change', syncReducedMotion);
});

// The controller computes visible + progress from the activity flag. It reads
// the animation mode from `reducedMotion()` lazily each time it shows the bar,
// so an OS-level toggle mid-session is honored on the next activity burst.
const { visible, progress } = useProgressController(isActive, {
  reducedMotion: () => reducedMotion.value,
});

// 0..1 → percentage string for the inline width.
const widthPct = computed(() => `${Math.round(progress.value * 100)}%`);
// aria-valuenow as an integer 0..100.
const ariaNow = computed(() => Math.round(progress.value * 100));
</script>

<template>
  <!--
    Teleport to <body> so the fixed bar is never trapped inside a transformed/
    overflow-clipped ancestor (a CSS transform on an ancestor would re-root
    position:fixed). z-[100] sits above the app shell, TopBar, drawers, modals.
  -->
  <Teleport to="body">
    <div data-testid="global-progress-bar-root">
      <Transition name="oh-progress-fade">
        <div
          v-if="visible"
          class="fixed inset-x-0 top-0 z-[100] h-[3px] overflow-hidden bg-transparent"
          data-testid="global-progress-bar"
          role="progressbar"
          aria-label="Loading"
          :aria-valuemin="0"
          :aria-valuemax="100"
          :aria-valuenow="ariaNow"
        >
          <div
            class="h-full rounded-r-full bg-primary shadow-[0_0_8px_rgba(91,79,232,0.5)]"
            :class="reducedMotion ? '' : 'transition-[width] duration-200 ease-out'"
            :style="{ width: widthPct }"
            data-testid="global-progress-bar-fill"
          />
        </div>
      </Transition>
      <!--
        Visually-hidden live region. Announces "Loading…" while a request is in
        flight and clears on settle, so screen-reader users get the same signal
        the bar gives sighted users without a visual flash for fast calls.
      -->
      <span class="sr-only" role="status" aria-live="polite" data-testid="global-progress-bar-live">
        {{ visible ? t('common.a11y.loading') : '' }}
      </span>
    </div>
  </Teleport>
</template>

<style scoped>
/* Fade the bar out on settle; the controller snaps width to 100% first. */
.oh-progress-fade-leave-active {
  transition: opacity 0.3s ease;
}
.oh-progress-fade-leave-to {
  opacity: 0;
}
/* No enter transition — the bar should appear instantly once past the debounce. */

@media (prefers-reduced-motion: reduce) {
  .oh-progress-fade-leave-active {
    transition: none;
  }
}
</style>
